import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function ContactForm({ slug, primary, name }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", service: "" });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone && !form.email) {
      toast.error("Proporciona un teléfono o correo.");
      return;
    }
    if (!consent) {
      toast.error("Debes autorizar el tratamiento de tus datos.");
      return;
    }
    setSending(true);
    try {
      await api.post(`/public/card/${slug}/lead`, form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo enviar");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl bg-white border border-xuma-border p-8 text-center" data-testid="contact-success">
        <CheckCircle2 className="h-10 w-10 mx-auto" style={{ color: primary }} />
        <h3 className="mt-4 font-heading font-bold text-xl text-xuma-dark">¡Mensaje enviado!</h3>
        <p className="mt-2 text-sm text-xuma-muted">{name} recibió tus datos y te contactará pronto.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl bg-white border border-xuma-border p-6">
      <p className="text-xs uppercase tracking-[0.2em] text-xuma-muted mb-1">Contáctame</p>
      <h3 className="font-heading font-bold text-xl text-xuma-dark">Envíame un mensaje</h3>
      <div className="mt-5 space-y-3">
        <div>
          <Label className="mb-1 block text-sm">Nombre *</Label>
          <Input data-testid="lead-name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="mb-1 block text-sm">Teléfono</Label>
            <Input data-testid="lead-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+52..." />
          </div>
          <div>
            <Label className="mb-1 block text-sm">Correo</Label>
            <Input data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
        </div>
        <div>
          <Label className="mb-1 block text-sm">¿Qué necesitas?</Label>
          <Input data-testid="lead-service" value={form.service} onChange={(e) => set("service", e.target.value)} placeholder="Servicio de interés" />
        </div>
        <div>
          <Label className="mb-1 block text-sm">Mensaje</Label>
          <Textarea data-testid="lead-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} />
        </div>
        <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1">
          <input type="checkbox" data-testid="lead-consent" checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-xuma-border" style={{ accentColor: primary }} />
          <span className="text-xs leading-relaxed text-xuma-muted">
            He leído el{" "}
            <a href="/legal/aviso-de-privacidad" target="_blank" rel="noreferrer" className="underline" style={{ color: primary }}>Aviso de Privacidad</a>{" "}
            y autorizo el tratamiento de mis datos para atender esta solicitud.
          </span>
        </label>
        <Button type="submit" data-testid="lead-submit" disabled={sending || !consent}
          className="w-full h-12 rounded-2xl text-white gap-2 disabled:opacity-60" style={{ background: primary }}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar mensaje
        </Button>
      </div>
    </form>
  );
}
